import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { taskStatusConfigsApi } from '@/lib/api';

interface TaskStatus {
  id: string;
  name: string;
  slug: string;
  color: string;
  position: number;
  isDefault: boolean;
  isClosed: boolean;
}

interface DeleteStatusModalProps {
  status: TaskStatus;
  statuses: TaskStatus[];
  onClose: () => void;
}

export function DeleteStatusModal({ status, statuses, onClose }: DeleteStatusModalProps) {
  const queryClient = useQueryClient();
  const [reassignToId, setReassignToId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Filter out the status being deleted and the current selection
  const availableStatuses = statuses.filter(s => s.id !== status.id);

  // Set default reassign target to the first available status
  const defaultReassignId = availableStatuses[0]?.id || '';

  const deleteMutation = useMutation({
    mutationFn: (taskStatusConfigId: string) =>
      taskStatusConfigsApi.remove(taskStatusConfigId, reassignToId || defaultReassignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskStatusConfigs'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to delete status');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!reassignToId && !defaultReassignId) {
      setError('Please select a status to reassign tasks to');
      return;
    }
    setError(null);
    deleteMutation.mutate(status.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-elevated animate-scale-in overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-grey-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-error/10">
              <AlertTriangle className="w-5 h-5 text-error" />
            </div>
            <h2 className="text-heading-4 text-secondary">Delete Status?</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-grey-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-grey-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-6 space-y-4">
            <p className="text-sm text-grey-600">
              Are you sure you want to delete the <strong>{status.name}</strong> status? Any tasks using this status will need to be reassigned.
            </p>

            {/* Task Count and Reassign Select */}
            {availableStatuses.length > 0 && (
              <div>
                <label htmlFor="reassignTo" className="block text-sm font-medium text-secondary mb-2">
                  Reassign tasks to <span className="text-error">*</span>
                </label>
                <select
                  id="reassignTo"
                  value={reassignToId || defaultReassignId}
                  onChange={(e) => {
                    setReassignToId(e.target.value);
                    setError(null);
                  }}
                  className="input"
                >
                  {availableStatuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {availableStatuses.length === 0 && (
              <p className="text-sm text-error">
                Cannot delete this status: no other statuses available to reassign tasks to.
              </p>
            )}

            {error && (
              <p className="text-sm text-error">{error}</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-grey-50 border-t border-grey-100">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={deleteMutation.isPending || availableStatuses.length === 0}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={availableStatuses.length === 0 || deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Status'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
